import { useEffect, useState } from 'react';
import { ExternalLink, FileText, Loader2, X } from 'lucide-react';
import { Button } from '../atoms/Button';
import { cn } from '../../lib/utils';
import { openInSystemBrowser } from '../../office/dialog';
import { getPresignedDownloadUrl } from '../../api/s3';
import { PdfViewer } from './PdfViewer';
import type { Citation } from '../../chat/types';

interface Props {
  citation: Citation | null;
  onClose: () => void;
  /** Insert the chunk text into the Word document at the cursor. */
  onInsertIntoDocument?: (html: string) => void;
  /**
   * Layout variant:
   *  - `overlay`: covers the chat (narrow task pane — default fallback)
   *  - `split`:   sits next to the chat in a side-by-side layout (wide pane)
   */
  variant?: 'overlay' | 'split';
}

type PdfState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'ready'; url: string }
  | { kind: 'error'; message: string };

/**
 * DB-citation / source preview.
 *
 * URL resolution order for the inline PDF viewer:
 *   1. citation.url if it's an absolute URL pointing at a `.pdf`
 *   2. citation.pdfPath → fetch presigned S3 download URL on demand
 *   3. citation.url even if not a PDF (external HTML source) — rendered as text preview
 *
 * Action buttons:
 *   - "Open in browser" → system browser via Office.context.ui.openBrowserWindow
 *     (works great for web-search results; PDFs also render natively in Safari/Chrome)
 *   - "Insert into document" → paste chunk text at Word cursor
 */
export function CitationPanel({
  citation,
  onClose,
  onInsertIntoDocument,
  variant = 'overlay',
}: Props): JSX.Element | null {
  const [pdfState, setPdfState] = useState<PdfState>({ kind: 'idle' });

  const citationId = citation?.id ?? null;
  const pdfPath = citation?.pdfPath;
  const url = citation?.url?.trim();
  const isDirectPdfUrl = !!url && /\.pdf(\?|#|$)/i.test(url);

  // Resolve the preview URL when the citation changes.
  useEffect(() => {
    let cancelled = false;
    if (!citationId) {
      setPdfState({ kind: 'idle' });
      return;
    }
    if (isDirectPdfUrl && url) {
      setPdfState({ kind: 'ready', url });
      return;
    }
    if (pdfPath) {
      setPdfState({ kind: 'loading' });
      getPresignedDownloadUrl(pdfPath)
        .then((signedUrl) => {
          if (cancelled) return;
          setPdfState({ kind: 'ready', url: signedUrl });
        })
        .catch((err: unknown) => {
          if (cancelled) return;
          // eslint-disable-next-line no-console
          console.warn('[CitationPanel] presigned URL fetch failed', err);
          setPdfState({
            kind: 'error',
            message: 'Could not load PDF preview. Use "Open in browser" to view the full document.',
          });
        });
      return;
    }
    setPdfState({ kind: 'idle' });
    return () => {
      cancelled = true;
    };
  }, [citationId, pdfPath, url, isDirectPdfUrl]);

  if (!citation) return null;

  const title = citation.title || citation.source || 'Source';
  const hasContent = !!citation.content?.trim();
  const hasExternalUrl = !!url && !isDirectPdfUrl;
  // Prefer the (possibly presigned) URL from state when popping out / opening.
  const openableUrl = pdfState.kind === 'ready' ? pdfState.url : url;
  const canOpen = !!openableUrl;

  const containerClass =
    variant === 'split'
      ? 'h-full flex flex-col bg-background border-l border-border min-w-0'
      : 'absolute inset-0 bg-background z-20 flex flex-col animate-in slide-in-from-right-5';

  return (
    <div className={containerClass}>
      <div className="flex items-center justify-between px-3 py-2 border-b border-border gap-2">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <FileText className="w-4 h-4 text-primary flex-shrink-0" />
          <div className="min-w-0">
            <div className="text-xs font-semibold truncate" title={title}>
              {title}
            </div>
            {citation.source && (
              <div className="text-[10px] text-muted-foreground truncate">{citation.source}</div>
            )}
          </div>
        </div>
        <Button variant="ghost" size="icon" onClick={onClose} title="Close">
          <X className="w-4 h-4" />
        </Button>
      </div>

      <div className="flex-1 overflow-hidden flex flex-col min-h-0">
        {pdfState.kind === 'loading' && (
          <div className="flex-1 flex items-center justify-center text-xs text-muted-foreground">
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            Loading PDF…
          </div>
        )}
        {pdfState.kind === 'ready' && (
          <PdfViewer
            url={pdfState.url}
            initialPage={citation.page}
            onError={(err) =>
              setPdfState({
                kind: 'error',
                message:
                  err.message?.includes('CORS') || err.message?.includes('fetch')
                    ? 'Inline preview blocked by the source. Use "Open in browser" to view the full document.'
                    : `Inline preview failed (${err.message}). Use "Open in browser" to view the full document.`,
              })
            }
          />
        )}
        {(pdfState.kind === 'idle' || pdfState.kind === 'error') && (
          <>
            {pdfState.kind === 'error' && (
              <div className="px-3 pt-3 text-[11px] text-amber-700 bg-amber-50 border-b border-amber-200 py-2">
                {pdfState.message}
              </div>
            )}
            {hasContent ? (
              <div className="flex-1 overflow-y-auto px-3 py-3 scrollbar-thin">
                <div className="prose-sm max-w-none text-sm text-foreground whitespace-pre-wrap leading-relaxed">
                  {citation.content}
                </div>
                {typeof citation.relevanceScore === 'number' && (
                  <div className="mt-4 text-[11px] text-muted-foreground">
                    Relevance score: {Math.round(citation.relevanceScore * 100) / 100}
                  </div>
                )}
              </div>
            ) : (
              <div className="flex-1 flex items-center justify-center text-xs text-muted-foreground italic px-6 text-center">
                No preview available for this citation.
              </div>
            )}
          </>
        )}
      </div>

      {(canOpen || hasExternalUrl || (hasContent && onInsertIntoDocument)) && (
        <div className="border-t border-border px-3 py-2 flex flex-col gap-1.5">
          {canOpen && (
            <Button
              variant="outline"
              size="sm"
              className={cn('w-full justify-start')}
              onClick={() => openInSystemBrowser(openableUrl!)}
            >
              <ExternalLink className="w-3.5 h-3.5 mr-2" />
              Open source in browser
            </Button>
          )}
          {hasContent && onInsertIntoDocument && (
            <Button
              variant="default"
              size="sm"
              className="w-full justify-start"
              onClick={() => onInsertIntoDocument(citation.content ?? '')}
            >
              Insert into document
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
